import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Circle, WarningCircle, ArrowLeft, PaperPlaneRight, FloppyDisk, Lock, Plus, X } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { sealList, SEAL_TYPES } from "@/lib/seals";

const lbl = "text-[11px] font-semibold uppercase tracking-wider text-slate-500";
const fld = "mt-1 w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
const roFld = "mt-1 w-full h-10 px-3 bg-slate-100 border border-slate-200 rounded-md text-sm text-slate-500";
// Seal rows sit in a flex row, so they must NOT carry `w-full` / `mt-1` like the
// stacked form fields above — that combination let the type <select> take the width
// and squeezed the seal-number input down to nothing (reported 2026-10-13: "seal
// number couldn't be fetched" — the value was there, just invisible).
const sealFld = "h-10 px-3 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
const sealFldRO = "h-10 px-3 bg-slate-100 border border-slate-200 rounded-md text-sm text-slate-500";

export default function PortalSI() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sections, setSections] = useState(null);
  const [active, setActive] = useState("parties");
  const [missing, setMissing] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const timers = useRef({});

  useEffect(() => {
    portalApi.get(`/portal/shipments/${id}/si`)
      .then(({ data }) => { setData(data); setSections(data.sections); setMissing(data.missing || []); setReadOnly(data.read_only); setSavedAt(data.updated_at); })
      .catch((e) => { toast.error(formatApiError(e)); navigate(`/portal/shipments/${id}`); });
  }, [id, navigate]);

  if (!data || !sections) return <div className="text-slate-400 text-sm">Loading SI…</div>;

  const hazardous = data.job?.hazardous;
  const STEPS = [
    { key: "parties", label: "Parties" },
    { key: "bl", label: "B/L details" },
    { key: "cargo", label: "Cargo" },
    { key: "containers", label: "Containers" },
    ...(hazardous ? [{ key: "dg", label: "Dangerous goods" }] : []),
    { key: "references", label: "References" },
    { key: "review", label: "Review & submit" },
  ];

  const stepState = (key) => {
    const s = sections;
    if (key === "parties") return s.parties?.shipper?.name && s.parties?.consignee?.name ? "done" : "todo";
    if (key === "bl") return s.bl?.bl_type ? "done" : "todo";
    if (key === "cargo") return s.cargo?.description_of_goods ? "done" : "todo";
    if (key === "containers") return "done";
    if (key === "dg") return s.dg?.un_number ? "done" : "todo";
    if (key === "references") return "done";
    if (key === "review") return missing.length === 0 ? "ready" : "todo";
    return "todo";
  };
  const doneCount = STEPS.filter((s) => s.key !== "review" && stepState(s.key) === "done").length;
  const totalCount = STEPS.length - 1;

  // Debounced per-section autosave.
  const save = (sectionKey, nextSections) => {
    if (readOnly) return;
    clearTimeout(timers.current[sectionKey]);
    timers.current[sectionKey] = setTimeout(async () => {
      setSaving(true);
      try {
        const payload = sectionKey === "containers"
          ? { section: "containers", data: { containers: nextSections.containers } }
          : { section: sectionKey, data: nextSections[sectionKey] };
        const { data: res } = await portalApi.patch(`/portal/shipments/${id}/si`, payload);
        setMissing(res.missing || []);
        setSavedAt(res.updated_at);
      } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    }, 700);
  };

  const setSec = (key, patch) => {
    setSections((prev) => {
      const next = { ...prev, [key]: { ...(prev[key] || {}), ...patch } };
      save(key, next);
      return next;
    });
  };
  const setParty = (who, patch) => {
    setSections((prev) => {
      const next = { ...prev, parties: { ...prev.parties, [who]: { ...(prev.parties?.[who] || {}), ...patch } } };
      save("parties", next);
      return next;
    });
  };
  const setContainer = (i, patch) => {
    setSections((prev) => {
      const rows = [...(prev.containers || [])];
      rows[i] = { ...rows[i], ...patch };
      const next = { ...prev, containers: rows };
      save("containers", next);
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await portalApi.post(`/portal/shipments/${id}/si/submit`);
      toast.success("Shipping Instruction submitted — our team will review it.");
      navigate(`/portal/shipments/${id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const PartyBlock = ({ who, title }) => (
    <div>
      <div className="text-sm font-semibold text-slate-800 mb-2">{title}</div>
      <div className="grid gap-3">
        <div><label className={lbl}>Name</label><input disabled={readOnly} value={sections.parties?.[who]?.name || ""} onChange={(e) => setParty(who, { name: e.target.value })} className={readOnly ? roFld : fld} /></div>
        <div><label className={lbl}>Address</label><textarea disabled={readOnly} rows={2} value={sections.parties?.[who]?.address || ""} onChange={(e) => setParty(who, { address: e.target.value })} className={(readOnly ? roFld : fld) + " h-auto py-2"} /></div>
      </div>
    </div>
  );

  return (
    <div>
      <button onClick={() => navigate(`/portal/shipments/${id}`)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={15} /> Back to shipment</button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Shipping Instruction</h1>
          <p className="text-slate-500 text-sm mt-1">{data.job?.job_number} · {data.job?.pol_name} → {data.job?.pod_name} · {data.job?.mode}</p>
        </div>
        <div className="text-[11px] text-slate-400 inline-flex items-center gap-2">
          {saving ? "Saving…" : savedAt ? <><FloppyDisk size={13} /> Draft saved</> : null}
        </div>
      </div>

      {data.status === "submitted" && <Banner tone="blue" text="This SI has been submitted and is awaiting review. You'll be notified once it's accepted or if changes are needed." />}
      {data.status === "accepted" && <Banner tone="green" text="This SI has been accepted by our team." />}
      {data.status === "returned" && <Banner tone="amber" text={`Changes requested: ${data.returned_reason || "please review and resubmit."}`} />}
      {/* Closed for a reason other than the draft's own status — e.g. staff already
          lodged the SI with the carrier, or the draft B/L has been raised from it. */}
      {data.closed_reason && !["submitted", "accepted"].includes(data.status) && (
        <Banner tone="blue" text={data.closed_reason} />
      )}

      <div className="grid md:grid-cols-[220px_1fr] gap-5 mt-2">
        {/* Progress rail */}
        <aside className="bg-white border border-slate-200 rounded-xl p-3 h-max">
          <div className="px-2 pb-2 text-[11px] text-slate-500">{doneCount} of {totalCount} sections complete</div>
          <div className="h-1.5 mx-2 mb-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${(doneCount / totalCount) * 100}%` }} /></div>
          <nav className="space-y-0.5">
            {STEPS.map((s) => {
              const stt = stepState(s.key);
              const on = active === s.key;
              return (
                <button key={s.key} onClick={() => setActive(s.key)} className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left ${on ? "bg-blue-50 text-blue-800 font-medium" : "text-slate-600 hover:bg-slate-50"}`}>
                  {stt === "done" || stt === "ready" ? <CheckCircle size={15} weight="fill" className="text-emerald-500 shrink-0" /> : stt === "todo" && s.key === "review" ? <WarningCircle size={15} className="text-amber-500 shrink-0" /> : <Circle size={15} className="text-slate-300 shrink-0" />}
                  {s.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Section body */}
        <section className="bg-white border border-slate-200 rounded-xl p-5 min-h-[300px]">
          {active === "parties" && (
            <div className="grid sm:grid-cols-2 gap-6">
              <PartyBlock who="shipper" title="Shipper" />
              <PartyBlock who="consignee" title="Consignee" />
              <PartyBlock who="notify" title="Notify Party" />
              <PartyBlock who="notify2" title="Second Notify (optional)" />
            </div>
          )}

          {active === "bl" && (
            <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
              <div><label className={lbl}>B/L Type</label>
                <select disabled={readOnly} value={sections.bl?.bl_type || ""} onChange={(e) => setSec("bl", { bl_type: e.target.value })} className={readOnly ? roFld : fld}>
                  {["Original", "Seaway", "Telex Release"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div><label className={lbl}>No. of Originals</label><input disabled={readOnly} type="number" min="0" value={sections.bl?.number_of_originals ?? 3} onChange={(e) => setSec("bl", { number_of_originals: Number(e.target.value) })} className={readOnly ? roFld : fld} /></div>
              <div><label className={lbl}>Freight Terms</label>
                <select disabled={readOnly} value={sections.bl?.freight_terms || ""} onChange={(e) => setSec("bl", { freight_terms: e.target.value })} className={readOnly ? roFld : fld}>
                  {["Prepaid", "Collect"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div><label className={lbl}>Place of Issue</label><input disabled={readOnly} value={sections.bl?.place_of_issue || ""} onChange={(e) => setSec("bl", { place_of_issue: e.target.value })} className={readOnly ? roFld : fld} /></div>
            </div>
          )}

          {active === "cargo" && (
            <div className="grid gap-4 max-w-2xl">
              <div><label className={lbl}>Description of Goods *</label><textarea disabled={readOnly} rows={4} value={sections.cargo?.description_of_goods || ""} onChange={(e) => setSec("cargo", { description_of_goods: e.target.value })} className={(readOnly ? roFld : fld) + " h-auto py-2"} /></div>
              <div><label className={lbl}>Marks &amp; Numbers</label><textarea disabled={readOnly} rows={2} value={sections.cargo?.marks_and_numbers || ""} onChange={(e) => setSec("cargo", { marks_and_numbers: e.target.value })} className={(readOnly ? roFld : fld) + " h-auto py-2"} placeholder="NO MARKS" /></div>
              <div className="max-w-xs"><label className={lbl}>HS Code</label><input disabled={readOnly} value={sections.cargo?.hs_code || ""} onChange={(e) => setSec("cargo", { hs_code: e.target.value })} className={readOnly ? roFld : fld} /></div>
            </div>
          )}

          {active === "containers" && (
            <div>
              <div className="text-[11px] text-slate-500 mb-3 inline-flex items-start gap-1.5">
                <Lock size={12} className="mt-0.5 shrink-0" />
                <span>Container number &amp; type come from your booking. You can edit packages, weights, CBM and seals —
                  add a shipper or customs seal with <span className="font-medium">Add seal</span>.</span>
              </div>
              {/* One card per container rather than a wide table: the old 8-column
                  layout pushed CBM and the seals off-screen behind a horizontal
                  scrollbar, so customers never saw them (reported on JTSH0019). */}
              <div className="space-y-3">
                {(sections.containers || []).map((c, i) => {
                  const seals = sealList(c);
                  const rows = seals.length ? seals : [{ number: "", type: "Line" }];
                  const writeSeals = (list) => {
                    const clean = list.filter((s) => (s.number || "").trim() || list.length === 1);
                    const line = clean.find((s) => (s.type || "").toLowerCase() === "line");
                    setContainer(i, {
                      seal_numbers: clean,
                      seal_number: (line || clean[0] || {}).number || "",
                    });
                  };
                  return (
                    <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-semibold text-slate-800">{c.container_number || "—"}</span>
                        <span className="text-[11px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border border-slate-200 bg-slate-50 text-slate-500">
                          {c.equipment_code || "—"}
                        </span>
                        {c.package_type && <span className="text-xs text-slate-500">{c.package_type}</span>}
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                        <div>
                          <label className={lbl}>Packages</label>
                          <input disabled={readOnly} type="number" min="0" value={c.no_of_packages ?? 0}
                            onChange={(e) => setContainer(i, { no_of_packages: Number(e.target.value) })}
                            className={readOnly ? roFld : fld} />
                        </div>
                        <div>
                          <label className={lbl}>Net Wt (kg)</label>
                          <input disabled={readOnly} type="number" min="0" value={c.net_weight_kg ?? 0}
                            onChange={(e) => setContainer(i, { net_weight_kg: Number(e.target.value) })}
                            className={readOnly ? roFld : fld} />
                        </div>
                        <div>
                          <label className={lbl}>Gross Wt (kg)</label>
                          <input disabled={readOnly} type="number" min="0" value={c.gross_weight_kg ?? 0}
                            onChange={(e) => setContainer(i, { gross_weight_kg: Number(e.target.value) })}
                            className={readOnly ? roFld : fld} />
                        </div>
                        <div>
                          <label className={lbl}>CBM</label>
                          <input disabled={readOnly} type="number" min="0" step="0.001" value={c.cbm ?? 0}
                            onChange={(e) => setContainer(i, { cbm: Number(e.target.value) })}
                            className={readOnly ? roFld : fld} />
                        </div>
                      </div>

                      <div className="mt-3">
                        <label className={lbl}>Seals</label>
                        <div className="space-y-2 mt-1">
                          {rows.map((s, k) => (
                            <div key={k} className="flex items-center gap-2">
                              <input disabled={readOnly} value={s.number || ""} placeholder="Seal number"
                                onChange={(e) => writeSeals(rows.map((x, n) => (n === k ? { ...x, number: e.target.value } : x)))}
                                className={`${readOnly ? sealFldRO : sealFld} flex-1 min-w-0`} />
                              <select disabled={readOnly} value={s.type || "Line"}
                                onChange={(e) => writeSeals(rows.map((x, n) => (n === k ? { ...x, type: e.target.value } : x)))}
                                className={`${readOnly ? sealFldRO : sealFld} w-28 shrink-0`}>
                                {SEAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                              </select>
                              {!readOnly && rows.length > 1 && (
                                <button type="button" title="Remove this seal"
                                  onClick={() => writeSeals(rows.filter((_, n) => n !== k))}
                                  className="h-9 w-9 shrink-0 rounded-md border border-slate-300 text-slate-400 hover:text-rose-600 hover:border-rose-300 inline-flex items-center justify-center">
                                  <X size={13} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {!readOnly && rows.length < 6 && (
                          <button type="button"
                            onClick={() => writeSeals([...rows, { number: "", type: rows.some((x) => (x.type || "").toLowerCase() === "line") ? "Shipper" : "Line" }])}
                            className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-blue-700 hover:underline">
                            <Plus size={13} /> Add seal
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(sections.containers || []).length === 0 && (
                  <div className="rounded-lg border border-slate-200 bg-white py-8 text-center text-slate-400 text-sm">
                    No containers on this booking yet.
                  </div>
                )}
              </div>
            </div>
          )}

          {active === "dg" && (
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
              <div><label className={lbl}>UN Number</label><input disabled={readOnly} value={sections.dg?.un_number || ""} onChange={(e) => setSec("dg", { un_number: e.target.value })} className={readOnly ? roFld : fld} /></div>
              <div><label className={lbl}>IMO / IMDG Class</label><input disabled={readOnly} value={sections.dg?.imo_class || ""} onChange={(e) => setSec("dg", { imo_class: e.target.value })} className={readOnly ? roFld : fld} /></div>
              <div className="sm:col-span-2"><label className={lbl}>Proper Shipping Name</label><input disabled={readOnly} value={sections.dg?.proper_shipping_name || ""} onChange={(e) => setSec("dg", { proper_shipping_name: e.target.value })} className={readOnly ? roFld : fld} /></div>
              <div><label className={lbl}>Packing Group</label><input disabled={readOnly} value={sections.dg?.packing_group || ""} onChange={(e) => setSec("dg", { packing_group: e.target.value })} className={readOnly ? roFld : fld} /></div>
              <div><label className={lbl}>24h Emergency Contact</label><input disabled={readOnly} value={sections.dg?.emergency_contact || ""} onChange={(e) => setSec("dg", { emergency_contact: e.target.value })} className={readOnly ? roFld : fld} /></div>
            </div>
          )}

          {active === "references" && (
            <div className="grid gap-4 max-w-xl">
              <div><label className={lbl}>Your Reference</label><input disabled={readOnly} value={sections.references?.customer_reference || ""} onChange={(e) => setSec("references", { customer_reference: e.target.value })} className={readOnly ? roFld : fld} /></div>
              <div><label className={lbl}>PO Number</label><input disabled={readOnly} value={sections.references?.po_number || ""} onChange={(e) => setSec("references", { po_number: e.target.value })} className={readOnly ? roFld : fld} /></div>
              <div><label className={lbl}>Remarks</label><textarea disabled={readOnly} rows={3} value={sections.references?.remarks || ""} onChange={(e) => setSec("references", { remarks: e.target.value })} className={(readOnly ? roFld : fld) + " h-auto py-2"} /></div>
            </div>
          )}

          {active === "review" && (
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-3">Review &amp; submit</div>
              {missing.length > 0 ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3">
                  Please complete these before submitting: <span className="font-semibold">{missing.join(", ")}</span>.
                </div>
              ) : (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-4 py-3">Everything looks complete. Submit your SI for our team to review.</div>
              )}
              <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                <ReviewRow k="Shipper" v={sections.parties?.shipper?.name} />
                <ReviewRow k="Consignee" v={sections.parties?.consignee?.name} />
                <ReviewRow k="B/L Type" v={sections.bl?.bl_type} />
                <ReviewRow k="Freight" v={sections.bl?.freight_terms} />
                <ReviewRow k="Goods" v={sections.cargo?.description_of_goods} />
                <ReviewRow k="Containers" v={`${(sections.containers || []).length}`} />
              </div>
              {/* Seals are the detail most often mistyped, so show them back before
                  the customer commits — the old summary only gave a count. */}
              {(sections.containers || []).length > 0 && (
                <div className="mt-3 rounded-md border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Container</th>
                        <th className="px-3 py-2 text-left">Seals</th>
                        <th className="px-3 py-2 text-right">Pkgs</th>
                        <th className="px-3 py-2 text-right">Gross (kg)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(sections.containers || []).map((c, i) => {
                        const s = sealList(c);
                        return (
                          <tr key={i}>
                            <td className="px-3 py-2 font-mono text-slate-700">{c.container_number || "—"}</td>
                            <td className="px-3 py-2 text-slate-600">
                              {s.length ? s.map((x) => `${x.number}${(x.type || "").toLowerCase() === "line" ? "" : ` (${x.type})`}`).join(", ")
                                : <span className="text-amber-600">no seal entered</span>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">{c.no_of_packages ?? 0}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">{Number(c.gross_weight_kg || 0).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {!readOnly && (
                <div className="flex justify-end mt-5">
                  <button onClick={submit} disabled={busy || missing.length > 0} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold disabled:opacity-50">
                    <PaperPlaneRight size={16} /> {busy ? "Submitting…" : "Submit Shipping Instruction"}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Banner({ tone, text }) {
  const cls = tone === "green" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-blue-200 bg-blue-50 text-blue-800";
  return <div className={`rounded-md border ${cls} text-sm px-4 py-3 mb-4`}>{text}</div>;
}
function ReviewRow({ k, v }) {
  return <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5"><span className="text-slate-500">{k}</span><span className="text-slate-800 font-medium text-right truncate">{v || "—"}</span></div>;
}
